import { Button, Card, CardContent, CardFooter, CardDescription, CardHeader, CardTitle, Input, Label } from '@lensora/ui'
import * as React from 'react'
import { Navigate, useNavigate, useSearchParams, Link } from 'react-router-dom'


import { GoogleIcon } from '@/components/google-icon'
import { authClient } from '@/lib/auth'
import { useAuth } from '@/providers/auth-provider'

type Mode = 'signin' | 'forgot' | 'forgot-sent'

export function LoginPage() {
  const auth = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const justConfirmed = searchParams.get('confirmed') === '1'
  const [mode, setMode] = React.useState<Mode>('signin')
  const [email, setEmail] = React.useState('')
  const [password, setPassword] = React.useState('')
  const [error, setError] = React.useState<string | null>(null)
  const [isLoading, setIsLoading] = React.useState(false)
  const [isGoogleLoading, setIsGoogleLoading] = React.useState(false)

  if (auth.status === 'authenticated') {
    return <Navigate to="/dashboard" replace />
  }

  async function handleSignIn(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setIsLoading(true)
    try {
      await auth.signIn(email, password)
      navigate('/dashboard', { replace: true })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign in failed')
    } finally {
      setIsLoading(false)
    }
  }

  async function handleGoogleSignIn() {
    setError(null)
    setIsGoogleLoading(true)
    try {
      await authClient.signInWithOAuth('google', window.location.origin)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Google sign in failed')
      setIsGoogleLoading(false)
    }
  }

  async function handleForgot(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setIsLoading(true)
    try {
      const redirectTo = `${window.location.origin}/reset-password`
      await authClient.resetPasswordForEmail(email, redirectTo)
      setMode('forgot-sent')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send reset email')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface-raised p-4">
      <div className="w-full max-w-sm space-y-6">
        <div className="text-center">
          <div className="mx-auto mb-4 h-10 w-10 rounded-lg bg-brand-primary" />
          <h1 className="text-2xl font-bold text-text">
            {mode === 'signin' ? 'Sign in' : 'Reset password'}
          </h1>
          <p className="mt-1 text-sm text-text-muted">
            {mode === 'signin' ? 'to your dashboard' : "we'll send you a recovery link"}
          </p>
        </div>

        <Card>
          {mode === 'signin' && (
            <>
              <CardHeader>
                <CardTitle className="text-base">Welcome back</CardTitle>
                <CardDescription>Enter your credentials to continue</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {justConfirmed && (
                  <p className="rounded-lg border border-feedback-success/20 bg-feedback-success/5 px-3 py-2 text-sm text-feedback-success">
                    Your email is confirmed — sign in to continue.
                  </p>
                )}
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={handleGoogleSignIn}
                  disabled={isGoogleLoading || isLoading}
                >
                  <GoogleIcon />
                  {isGoogleLoading ? 'Connecting…' : 'Continue with Google'}
                </Button>

                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t border-border" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-surface px-2 text-text-muted">or</span>
                  </div>
                </div>

                <form onSubmit={handleSignIn} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@example.com"
                      autoComplete="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="password">Password</Label>
                      <button
                        type="button"
                        onClick={() => { setError(null); setMode('forgot') }}
                        className="text-xs text-brand-accent hover:underline"
                      >
                        Forgot password?
                      </button>
                    </div>
                    <Input
                      id="password"
                      type="password"
                      placeholder="••••••••"
                      autoComplete="current-password"
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                  </div>
                  {error && <p className="text-sm text-feedback-error">{error}</p>}
                  <Button type="submit" className="w-full" disabled={isLoading || isGoogleLoading}>
                    {isLoading ? 'Signing in…' : 'Sign in'}
                  </Button>
                </form>
              </CardContent>
              <CardFooter className="justify-center">
                <p className="text-sm text-text-muted">
                  Don't have an account?{' '}
                  <Link to="/signup" className="text-brand-accent hover:underline font-medium">
                    Sign up
                  </Link>
                </p>
              </CardFooter>
            </>
          )}

          {mode === 'forgot' && (
            <>
              <CardHeader>
                <CardTitle className="text-base">Forgot your password?</CardTitle>
                <CardDescription>Enter your email and we'll send a recovery link.</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleForgot} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="forgot-email">Email</Label>
                    <Input
                      id="forgot-email"
                      type="email"
                      placeholder="you@example.com"
                      autoComplete="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </div>
                  {error && <p className="text-sm text-feedback-error">{error}</p>}
                  <Button type="submit" className="w-full" disabled={isLoading}>
                    {isLoading ? 'Sending…' : 'Send recovery link'}
                  </Button>
                  <button
                    type="button"
                    onClick={() => { setError(null); setMode('signin') }}
                    className="w-full text-center text-sm text-text-muted hover:text-text"
                  >
                    Back to sign in
                  </button>
                </form>
              </CardContent>
            </>
          )}

          {mode === 'forgot-sent' && (
            <>
              <CardHeader>
                <CardTitle className="text-base">Check your email</CardTitle>
                <CardDescription>
                  If an account exists for <strong>{email}</strong>, you'll receive a recovery link shortly.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <button
                  type="button"
                  onClick={() => setMode('signin')}
                  className="w-full text-center text-sm text-text-muted hover:text-text"
                >
                  Back to sign in
                </button>
              </CardContent>
            </>
          )}
        </Card>
      </div>
    </div>
  )
}
